import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import {
  DndContext,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

// ⚠️ O MESMO detector do A3 — ❌ nunca um segundo. A pergunta é "este aparelho
// tem ponteiro fino?", e ela já tem dono neste projeto.
import { usePodePairar } from '@/hooks';

import {
  Avatar,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  Select,
} from '@/components/ui';
import { EditableColumnHeader } from '@/components/kanban/EditableColumnHeader';
import {
  useKanbanColumns,
  type DefaultStage,
  type KanbanColumn as KanbanColumnInfo,
} from '@/lib/use-kanban-columns';
import { formatCurrencyCompact } from '@/lib/format';
import { cn } from '@/lib/cn';
import { CAMADA } from '@/lib/camadas';
import type { CardHealth } from '@/lib/card-health';

import { countItemsByHealth, resolveKanbanDrop } from './kanban-utils';

// ─── Props públicas ────────────────────────────────────────────────────

/**
 * Props passadas pra cada card. O componente do card é responsável por:
 *   - `useDraggable({ id: item.id })` (a KanbanBoard só monta o DndContext)
 *   - aplicar `data-testid="kanban-card-{id}"` no root
 *   - aplicar `data-health` quando relevante (a board usa essa info pra dimming via `healthFilter`)
 *   - decidir como dimar via `healthFilter` quando setado
 *
 * Trade-off: cada workflow tem boilerplate (`useDraggable` + classes). Compensa
 * pela flexibilidade — DealCard precisa de avatar, valor, badge prioridade; OsCard
 * precisa de técnico, endereço, agendamento. Forçar wrapper único seria contrição.
 */
export interface KanbanCardProps<T> {
  item: T;
  /**
   * Filter de health ativo na board. Card decide o que fazer:
   *   - se `null`: render normal
   *   - se `'overdue'|'idle'|'healthy'`: card cuja health != filter dimma (opacity-30)
   *
   * Aplicar via `data-dimmed="true"` no root pra atributo ser observável em tests.
   */
  healthFilter: CardHealth | null;
  /** Coluna onde o card vive — útil pra variant/styling contextual. */
  column: KanbanColumnInfo;
}

export interface KanbanBoardProps<T extends { id: string }> {
  /** Slug do workflow — chave do `useKanbanColumns` e dos overrides de label. */
  workflowSlug: string;
  /** Stages padrão imutáveis em código (DEAL_STAGES, OS_STATUS_COLUMNS, etc.). */
  defaultStages: DefaultStage[];

  /** Itens a exibir (já filtrados pelo container — board não filtra). */
  items: T[];
  /** Extrai o slug da coluna atual do item (ex: `d.fase`, `os.status`). */
  getItemStage: (item: T) => string;

  /** Componente que renderiza um card. */
  cardComponent: ComponentType<KanbanCardProps<T>>;

  /**
   * Chamado quando drag-and-drop completa um move válido.
   * Board já resolveu via `resolveKanbanDrop` (no-ops bloqueados). O caller
   * dispara mutation backend + invalidate cache.
   */
  onStageChange: (input: {
    item: T;
    fromSlug: string;
    toSlug: string;
    fromLabel: string;
    toLabel: string;
  }) => void | Promise<void>;

  // ─── Opcionais — features das 12 regras ────────────────────────────

  /** Admin: pode renomear labels, criar/excluir colunas custom, ver automações. Default false. */
  isAdmin?: boolean;

  /** Esconde colunas por slug (ex: hideGanhoPerdido). */
  hiddenStages?: string[];

  /** Mostra soma de valor no header (regra b — financeiro). Default false. */
  showColumnTotals?: boolean;
  /** Extrai valor PONTUAL do item (R$). Default 0. Usado se `showColumnTotals` true. */
  getItemValue?: (item: T) => number;
  /**
   * PF12 — extrai o valor RECORRENTE do item (R$/mês). Quando provido e
   * `showColumnTotals`, o header ganha uma 2ª linha "R$ X/mês" com a soma das
   * mensalidades (locação + recorrentes) — nunca somada no total pontual.
   */
  getItemValueRecorrente?: (item: T) => number;

  /** Health getter — quando provido, board renderiza badges agregados + filter UX. */
  getItemHealth?: (item: T) => CardHealth | null;

  /** EmptyState quando `items.length === 0`. Default copy genérico. */
  emptyState?: {
    icon?: ReactNode;
    title: string;
    description?: string;
    action?: ReactNode;
  };

  /** Slot acima do board (botões admin, ex: "⚙ Automações"). Renderizado em flex-end. */
  headerActions?: ReactNode;

  /**
   * Responsável do workflow — exibido no header como "Gerenciado por: Nome".
   * Bloco E (FEAT-V2-011). Opcional: containers que não buscam workflow detail
   * podem omitir e a board renderiza sem o banner.
   */
  workflowOwner?: { nome: string; fotoUrl: string | null } | null;

  /** Slot abaixo do board (modais, painéis slide-in que vivem fora do DnD). */
  afterContent?: ReactNode;

  /**
   * Copy da mensagem "vazio" exibida dentro de uma coluna sem cards.
   * Default 'Nenhum card'.
   */
  emptyColumnText?: string;

  /**
   * UIUX-Comercial U2 (aditivo): com `true`, o board NUNCA troca as colunas
   * pelo EmptyState global — com zero itens as colunas aparecem com seus
   * rótulos (a estrutura do fluxo fica visível). Default false = clássico.
   */
  mostrarColunasVazias?: boolean;

  /**
   * UIUX-Comercial U2 (aditivo): conteúdo customizado pro estado vazio de
   * UMA coluna específica (chave = slug). Ex.: CTA "+ Novo lead" dentro da
   * coluna "Novo". Colunas sem entrada usam `emptyColumnText`.
   */
  emptyColumnHints?: Record<string, ReactNode>;

  /**
   * FX-19: modo "cabe na largura". As colunas usam `flex-1` (dividem a largura no
   * desktop, sem rolagem horizontal) e cada coluna rola **verticalmente** dentro de
   * si. Default false = comportamento clássico (colunas `w-72` + rolagem horizontal),
   * usado pelo Comercial. Opt-in do Quadro de Compras (5 colunas).
   */
  fitColumns?: boolean;
  /**
   * FX-32: modo "altura cheia". O board vira flex-col que preenche a altura do pai;
   * as colunas rolam **verticalmente** e a **barra horizontal fica fixa no rodapé**
   * (não some com muitas colunas). Default false = clássico (Comercial/Financeiro/
   * Manutenção intactos). Opt-in do Quadro de Compras. O pai deve dar altura
   * (ex.: `h-[calc(100vh-Nrem)] flex flex-col`).
   */
  fillHeight?: boolean;
}

// ─── Defaults & constants ──────────────────────────────────────────────

const DEFAULT_GET_VALUE = () => 0;

// ─── Componente ────────────────────────────────────────────────────────

/**
 * `<KanbanBoard>` — base universal do Padrão Kanban IGM (12 regras).
 *
 * Genérico sobre `T extends { id: string }`. Provê:
 *
 *   (a) admin renomeia labels via `EditableColumnHeader` (custom + padrão)
 *   (b) soma de valor no header da coluna (opt-in via `showColumnTotals`)
 *   (c) badges agregados de saúde (overdue/idle) quando `getItemHealth` fornecido
 *   (d) drag-and-drop entre colunas com `resolveKanbanDrop` (no-ops bloqueados)
 *   (e) admin CRUD de colunas custom — "+ Nova coluna" + ✕ excluir + modal de mover cards
 *   (f) UI usa terminologia "Fluxo" (não "Pipeline")
 *   (g) toggle visual de health filter — click no badge dimma cards não-matching
 *   (h) suporte a colunas padrão (variant) + custom (sempre `neutral`)
 *   (i) limite suave de 12 colunas
 *   (j) `headerActions` slot pra botões opcionais (ex: Automações)
 *   (k) `afterContent` slot pra modais/painéis que vivem fora do DnD
 *   (l) empty state customizável
 *
 * O componente do card (passado via `cardComponent`) é responsável por:
 *   - `useDraggable({ id: item.id })` no root
 *   - `data-testid="kanban-card-{id}"`
 *   - dimming quando `healthFilter !== null && getCardHealth(item) !== healthFilter`
 *
 * Tests do consumer cobrem comportamento — testes da board cobrem
 * apenas o que ela faz por conta própria (colunas, DnD, badges agregados,
 * CRUD de colunas, totais).
 */
/**
 * 🔴 A1 — A BARRA DE ROLAGEM HORIZONTAL, ALCANÇÁVEL DO TOPO (25/09/2026).
 *
 * **A queixa do Matheus:** *"a barra horizontal no funil de negócio e funil de
 * leads fica só no início da página. quero mesmo estando no topo da mesma que a
 * barra horizontal já apareça"*.
 *
 * ⚠️ **MEDIDO no beta antes de mexer** — e o mecanismo de altura NÃO está com
 * defeito, está fazendo exatamente o que foi desenhado:
 *
 * | Janela | `max(1020px, 100dvh−57)` | rolar para achar a barra |
 * |---|---|---|
 * | 1920×**1080** | max(1020, 1023) | **0** |
 * | 1600×**900**  | max(**1020**, 843) | **177px** |
 * | 1366×**768**  | max(**1020**, 711) | **309px** |
 *
 * 🔴 **O piso de 1020px é DECISÃO DELE, de 08/08/2026**, tomada porque *"1 card
 * por coluna em 1366×768"* era a queixa daquele dia. **Os dois pedidos
 * conflitam:** o piso é o que empurra a página a rolar, e a página rolando é o
 * que esconde a barra nativa — que mora na borda de BAIXO do quadro.
 *
 * ✅ **Por isso a cura não mexe na altura nem escolhe entre os dois pedidos:**
 * ela põe um segundo controle de rolagem **ACIMA** do quadro, espelhando a
 * largura dele. ⚠️ **Continua sendo do QUADRO, colada a ele** — não é barra
 * grudada na borda da janela, que é o que a régua deste projeto proíbe.
 *
 * ❌ **Não substitui a nativa de baixo:** quem já rolava por lá continua. As
 * duas andam juntas.
 *
 * ⚠️ **No celular ela não aparece:** lá a interação é arrastar a coluna com o
 * dedo (`snap-x`), e uma barra de 10px seria alvo menor que a régua de 44px
 * manda — **controle que não dá para acertar é pior que controle nenhum.**
 */
/**
 * 🔴 A ALTURA DA FAIXA DEPENDE DO APONTADOR, não do tamanho da tela
 * (26/09/2026 — correção do Matheus olhando o beta: *"a barra ta na posição
 * certa, mas essa 'moldura' dela ta muito grande"*).
 *
 * ⚠️ **A culpa foi de aplicar uma régua de DEDO a um MOUSE.** O piso de 44px é
 * alvo de toque; com ponteiro fino ele vira uma moldura de 44px em volta de um
 * filete — **ocupa o rodapé inteiro para mostrar quatro pixels de cursor.**
 *
 * ✅ **O corte é por CAPACIDADE DE APONTADOR** (`usePodePairar`, o mesmo
 * detector do A3), ❌ **não por largura de tela**: um tablet grande tem dedo, e
 * um laptop estreito tem mouse. ⚠️ **E é o detector que já existe — criar um
 * segundo é exatamente o que a regra do motor único proíbe.**
 */
export const ALTURA_BARRA_RODAPE_TOQUE = 44;
export const ALTURA_BARRA_RODAPE_MOUSE = 14;

/**
 * 🔴 UMA avaliação, UM número. O quadro devolve **exatamente** a altura que a
 * faixa ocupa — e por isso o valor é calculado no PAI e descido como
 * propriedade, em vez de cada um perguntar por si.
 * ⚠️ Dois lugares perguntando a mesma coisa dão a mesma resposta **até o dia em
 * que não dão** — e aí um card fica escondido atrás da faixa.
 */
// ⚠️ NÃO exportado: ele só serve a este arquivo, e exportar FUNÇÃO de um
// arquivo de componente quebra o fast refresh (o lint cobra, e o gate roda com
// `--max-warnings 0`). As CONSTANTES continuam exportadas — elas são o contrato
// da altura, e o teste as lê.
function useAlturaDaBarraDoRodape(): number {
  return usePodePairar() ? ALTURA_BARRA_RODAPE_MOUSE : ALTURA_BARRA_RODAPE_TOQUE;
}

/** A largura mínima do cursor desenhado — abaixo disto não dá para agarrar. */
const CURSOR_MINIMO_PX = 40;

function BarraDeRolagemDoQuadro({
  alvo,
  altura,
}: {
  alvo: React.RefObject<HTMLDivElement | null>;
  altura: number;
}) {
  /**
   * 🔴 A PISTA É GUARDADA EM ESTADO, não em `ref` — e isto é o conserto de um
   * cursor de LARGURA ZERO que a própria prova de navegador pegou em 26/09.
   *
   * ⚠️ **A armadilha:** a largura do cursor é proporcional à largura da pista,
   * e a pista só existe **depois** que `transborda` vira `true` — mas quem
   * mede é o efeito que roda ANTES, quando o componente ainda devolve `null`.
   * Com `ref`, `pistaRef.current` era `null` na única medição que acontecia, a
   * pista ficava em 0, e **o cursor nascia invisível e nunca mais remedia**.
   *
   * ✅ Em estado, o nó **avisa quando aparece**, e aí ele é medido e observado.
   * ⚠️ E o `ResizeObserver` do quadro não servia: ele observa o QUADRO, não a
   * pista — a pista muda de largura quando a sidebar recolhe, e o quadro não
   * necessariamente.
   */
  const [pistaEl, setPistaEl] = useState<HTMLDivElement | null>(null);
  /** O que o quadro tem, o que ele mostra e o quanto já rolou — os três números que desenham o cursor. */
  const [total, setTotal] = useState(0);
  const [visivel, setVisivel] = useState(0);
  const [rolado, setRolado] = useState(0);
  const [pista, setPista] = useState(0);
  const [transborda, setTransborda] = useState(false);
  /** 🔴 O quadro está NA TELA? Barra fixa de um quadro fora de vista é ruído. */
  const [quadroNaTela, setQuadroNaTela] = useState(true);
  /** Arrastando o cursor: de onde começou, em px de pista e de rolagem. */
  const arrasto = useRef<{ x: number; rolado: number } | null>(null);

  const medir = useCallback(() => {
    const el = alvo.current;
    if (!el) return;
    setTotal(el.scrollWidth);
    setVisivel(el.clientWidth);
    setRolado(el.scrollLeft);
    // ✅ Só existe quando há o que rolar. **Medido em 25/09:** o Funil tem 9
    // colunas e 2752px de conteúdo; o Leads tem 5 e 1536px — **em tela larga o
    // Leads não tem o que rolar**, e barra inerte no rodapé é ruído.
    setTransborda(el.scrollWidth > el.clientWidth + 4);
  }, [alvo]);

  useEffect(() => {
    medir();
    const el = alvo.current;
    if (!el) return;
    // 🔴 `ResizeObserver` e não `window.resize`: a largura do quadro muda quando
    // a SIDEBAR recolhe e quando uma coluna é criada — nenhum dos dois dispara
    // `resize` da janela.
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    for (const filho of Array.from(el.children)) ro.observe(filho);
    // ⚠️ A barra é FIXA na janela; o quadro sai de vista quando a página rola
    // (e ela rola: o piso de 1020px do quadro é maior que a janela em 900 e em
    // 768). Sem isto, a barra continuaria no rodapé comandando um quadro que
    // ninguém está vendo.
    const io = new IntersectionObserver(([e]) => setQuadroNaTela(e.isIntersecting), {
      threshold: 0,
    });
    io.observe(el);
    return () => {
      ro.disconnect();
      io.disconnect();
    };
  }, [alvo, medir]);

  /**
   * O quadro rolou (dedo, roda, arrastar) → o cursor acompanha.
   *
   * ✅ **Com o cursor DESENHADO some a trava de reentrância** que a versão do
   * espelho precisava: a posição dele é DERIVADA de `scrollLeft`, não um
   * segundo estado que tem de ser mantido em dia. **Havia dois donos da mesma
   * verdade; agora há um.**
   */
  useEffect(() => {
    const el = alvo.current;
    if (!el) return;
    const aoRolar = () => setRolado(el.scrollLeft);
    el.addEventListener('scroll', aoRolar, { passive: true });
    return () => el.removeEventListener('scroll', aoRolar);
  }, [alvo]);

  /**
   * A pista mede a si mesma assim que existe, e se remede quando muda.
   * 🔴 **Um dono só para `pista`** — era ter dois lugares medindo (e um deles
   * cedo demais) que produzia o cursor de largura zero.
   */
  useEffect(() => {
    if (!pistaEl) return;
    const atualizar = () => setPista(pistaEl.clientWidth);
    atualizar();
    const ro = new ResizeObserver(atualizar);
    ro.observe(pistaEl);
    return () => ro.disconnect();
  }, [pistaEl]);

  /** Converte uma posição na pista em rolagem do quadro. */
  const rolarPara = useCallback(
    (esquerdaNaPista: number) => {
      const el = alvo.current;
      if (!el || pista <= 0) return;
      const larguraCursor = Math.max(CURSOR_MINIMO_PX, (visivel / total) * pista);
      const curso = pista - larguraCursor;
      if (curso <= 0) return;
      const fracao = Math.min(1, Math.max(0, esquerdaNaPista / curso));
      el.scrollLeft = fracao * (total - visivel);
    },
    [alvo, pista, total, visivel],
  );

  if (!transborda || !quadroNaTela) return null;

  // ── O cursor, em pixels de pista ────────────────────────────────────────
  const larguraCursor = pista > 0 ? Math.max(CURSOR_MINIMO_PX, (visivel / total) * pista) : 0;
  const curso = Math.max(0, pista - larguraCursor);
  const fracaoRolada = total > visivel ? rolado / (total - visivel) : 0;
  const esquerdaCursor = Math.min(curso, Math.max(0, fracaoRolada * curso));
  const percentoVisto = Math.round(Math.min(1, Math.max(0, fracaoRolada)) * 100);

  return (
    <div
      data-testid="kanban-barra-rodape"
      style={{
        zIndex: CAMADA.GRUDENTO,
        height: altura,
      }}
      /**
       * ⚠️ O `bottom` é CLASSE, não estilo em linha — e isto é uma correção
       * medida, não preferência de estilo. **Na primeira versão ele era estilo
       * em linha (`bottom: 'var(--igm-rodape-mobile-h, 0px)'`) com um
       * `sm:bottom-0` de classe ao lado.** 🔴 **Estilo em linha ganha de
       * qualquer classe:** o `sm:bottom-0` nunca valia, e a barra ficava
       * **80px acima do fundo da janela NO COMPUTADOR** — medido em 26/09 nos
       * quatro tamanhos, sempre 80. Era exatamente o que o Matheus recusou
       * ("quero ele bem ao final da tela"), só que discreto o bastante para
       * passar.
       *
       * 🔴 E o corte é `md`, não `sm`: quem ocupa o rodapé no celular é a
       * `MobileTabBar`, que é `md:hidden`. Com `sm:bottom-0` a barra desceria
       * em 640px e **passaria por cima da barra de abas entre 640 e 767px** —
       * a faixa do tablet em pé, que ninguém olha.
       */
      className="fixed inset-x-0 bottom-[var(--igm-rodape-mobile-h,0px)] md:bottom-0 flex items-center border-t border-line bg-paper/95 backdrop-blur-sm dark:border-line-dark dark:bg-paper-dark/95 px-4"
    >
      {/*
        🔴 O CURSOR É DESENHADO POR NÓS — e isto veio do OLHO do Matheus, em
        26/09/2026: *"no print, a faixa está vazia de ponta a ponta"*.

        ⚠️ **A versão anterior era um espelho com barra NATIVA**, e apostava que
        o sistema operacional pintaria o cursor. **A sonda daquele dia provou
        que a CÂMERA não fotografa cursor nenhum nesta máquina** — o que era
        verdade — **e eu concluí dela que o cursor provavelmente estava lá.**
        🔴 **Não estava.**

        ✅ **Régua que saiu disso:** *sonda que prova a cegueira do instrumento
        NÃO prova a presença da coisa.* Ela só autoriza escrever "este
        instrumento não responde"; a resposta tem de vir de outro — e veio, do
        olho humano.

        ✅ **E o desenho próprio devolve a pergunta à medição:** um elemento que
        NÓS desenhamos aparece no print. A prova de navegador passa a conferir
        largura e posição do cursor.
      */}
      <div
        ref={setPistaEl}
        data-testid="kanban-barra-rodape-pista"
        className="relative h-1.5 w-full cursor-pointer rounded-full bg-line dark:bg-line-dark"
        onPointerDown={(e) => {
          // Clique na PISTA salta para lá; o alvo do salto é o centro do cursor.
          if ((e.target as HTMLElement).dataset.testid === 'kanban-barra-rodape-cursor') return;
          const r = pistaEl?.getBoundingClientRect();
          if (r) rolarPara(e.clientX - r.left - larguraCursor / 2);
        }}
      >
        <div
          data-testid="kanban-barra-rodape-cursor"
          role="scrollbar"
          aria-orientation="horizontal"
          aria-label="Rolar o quadro na horizontal"
          aria-valuenow={percentoVisto}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
          style={{ width: larguraCursor, transform: `translateX(${esquerdaCursor}px)` }}
          className="absolute inset-y-0 left-0 rounded-full bg-ink-3 transition-colors hover:bg-ink-2 dark:bg-ink-dark-3 dark:hover:bg-ink-dark-2"
          onPointerDown={(e) => {
            // ⚠️ `setPointerCapture`: sem ele, arrastar rápido para fora da
            // faixa larga o cursor no meio do caminho.
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            arrasto.current = { x: e.clientX, rolado: esquerdaCursor };
          }}
          onPointerMove={(e) => {
            if (!arrasto.current) return;
            rolarPara(arrasto.current.rolado + (e.clientX - arrasto.current.x));
          }}
          onPointerUp={(e) => {
            (e.target as HTMLElement).releasePointerCapture(e.pointerId);
            arrasto.current = null;
          }}
          /* ✅ Teclado: o cursor recebe foco, e as setas rolam o quadro. */
          onKeyDown={(e) => {
            const el = alvo.current;
            if (!el) return;
            const passo = e.shiftKey ? visivel : 120;
            if (e.key === 'ArrowRight') {
              el.scrollLeft += passo;
              e.preventDefault();
            } else if (e.key === 'ArrowLeft') {
              el.scrollLeft -= passo;
              e.preventDefault();
            } else if (e.key === 'Home') {
              el.scrollLeft = 0;
              e.preventDefault();
            } else if (e.key === 'End') {
              el.scrollLeft = total - visivel;
              e.preventDefault();
            }
          }}
        />
      </div>
    </div>
  );
}

export function KanbanBoard<T extends { id: string }>(props: KanbanBoardProps<T>) {
  const {
    workflowSlug,
    defaultStages,
    items,
    getItemStage,
    cardComponent: CardComponent,
    onStageChange,
    isAdmin = false,
    hiddenStages,
    showColumnTotals = false,
    getItemValue = DEFAULT_GET_VALUE,
    getItemValueRecorrente,
    getItemHealth,
    emptyState,
    headerActions,
    workflowOwner,
    afterContent,
    emptyColumnText = 'Nenhum card',
    mostrarColunasVazias = false,
    emptyColumnHints,
    fitColumns = false,
    fillHeight = false,
  } = props;

  const {
    columns,
    getLabel,
    setLabel,
    addColumn,
    removeColumn,
    atSoftLimit,
    SOFT_LIMIT,
  } = useKanbanColumns(workflowSlug, defaultStages);

  const [healthFilter, setHealthFilter] = useState<CardHealth | null>(null);
  const [creatingColumn, setCreatingColumn] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState<KanbanColumnInfo | null>(null);

  /** A1: o container que rola na horizontal — a barra espelhada precisa dele. */
  const quadroRef = useRef<HTMLDivElement>(null);
  /**
   * 🔴 UMA avaliação da altura da faixa, aqui — e ela desce como propriedade.
   * ⚠️ O quadro devolve EXATAMENTE o que a faixa ocupa; se os dois
   * perguntassem por conta própria, bastaria um divergir para um card ficar
   * escondido atrás dela.
   */
  const alturaBarra = useAlturaDaBarraDoRodape();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // ─── Visibilidade & agrupamento ──────────────────────────────────────

  const columnsSemFiltro = useMemo<KanbanColumnInfo[]>(() => {
    if (!hiddenStages || hiddenStages.length === 0) return columns;
    const blocked = new Set(hiddenStages);
    return columns.filter((c) => !blocked.has(c.slug));
  }, [columns, hiddenStages]);

  // D-4 (FILA-05AGO): o quadro NÃO muda debaixo do dedo. Colunas e cards
  // atualizam sozinhos (refetch de 30s) — se a atualização chegasse no meio de
  // um arrasto, a coluna sob o cursor trocaria de lugar e o card cairia na
  // fase errada. Enquanto um drag está em curso, o board renderiza e resolve o
  // drop contra a FOTO de quando o gesto começou; o dado novo entra no drag
  // end/cancel.
  const [fotoDrag, setFotoDrag] = useState<{ items: T[]; columns: KanbanColumnInfo[] } | null>(
    null,
  );
  const columnsVisiveis = fotoDrag?.columns ?? columnsSemFiltro;
  const itemsVisiveis = fotoDrag?.items ?? items;

  const validColumnSlugs = useMemo(
    () => new Set(columnsVisiveis.map((c) => c.slug)),
    [columnsVisiveis]
  );

  const itemsPorColuna = useMemo(() => {
    const m = new Map<string, T[]>();
    for (const c of columnsVisiveis) m.set(c.slug, []);
    for (const i of itemsVisiveis) {
      const slug = getItemStage(i);
      const arr = m.get(slug);
      if (arr) arr.push(i);
      // Items em slug desconhecido (coluna deletada): ignorados visualmente
    }
    return m;
  }, [itemsVisiveis, columnsVisiveis, getItemStage]);

  // ─── Handlers ─────────────────────────────────────────────────────────

  function handleDragStart() {
    setFotoDrag({ items, columns: columnsSemFiltro });
  }

  function handleDragCancel() {
    setFotoDrag(null);
  }

  function handleDragEnd(event: DragEndEvent) {
    // Resolve contra a foto: o destino é a coluna que a pessoa VIU ao soltar.
    const base = fotoDrag?.items ?? items;
    const move = resolveKanbanDrop(event, base, getItemStage, validColumnSlugs);
    setFotoDrag(null);
    if (!move) return;
    // A mutação usa o item ATUAL (o dado pode ter avançado durante o gesto);
    // a foto só decide QUAL item e PRA ONDE.
    const item = items.find((i) => i.id === move.itemId) ?? base.find((i) => i.id === move.itemId);
    if (!item) return;
    const fromSlug = getItemStage(item);
    if (fromSlug === move.novaFase) return;
    void onStageChange({
      item,
      fromSlug,
      toSlug: move.novaFase,
      fromLabel: getLabel(fromSlug),
      toLabel: getLabel(move.novaFase),
    });
  }

  function toggleHealthFilter(target: CardHealth) {
    setHealthFilter((current) => (current === target ? null : target));
  }

  async function handleConfirmDeleteColumn(targetSlug: string | null) {
    if (!columnToDelete) return;
    const itemsInColumn = items.filter((i) => getItemStage(i) === columnToDelete.slug);
    if (itemsInColumn.length > 0) {
      if (!targetSlug) return;
      for (const it of itemsInColumn) {
        await onStageChange({
          item: it,
          fromSlug: columnToDelete.slug,
          toSlug: targetSlug,
          fromLabel: getLabel(columnToDelete.slug),
          toLabel: getLabel(targetSlug),
        });
      }
    }
    await removeColumn(columnToDelete.slug);
    setColumnToDelete(null);
  }

  // ─── Empty state global ──────────────────────────────────────────────

  if (!mostrarColunasVazias && items.length === 0 && columns.length === defaultStages.length) {
    return (
      <Card variant="flat">
        <EmptyState
          icon={emptyState?.icon}
          title={emptyState?.title ?? 'Nenhum card'}
          description={emptyState?.description}
          action={emptyState?.action}
        />
      </Card>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────

  return (
    <>
      {(workflowOwner || isAdmin || headerActions) && (
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          {workflowOwner ? (
            <div
              // L-2: era um tamanho de 12 cravado em colchetes, abaixo do piso de
              // 13px do sistema. Tamanho solto em tela é como o piso se fura sem
              // ninguém ver. (O nome da classe não se escreve nem em comentário:
              // o scanner do Tailwind lê daqui e ressuscita a regra no CSS.)
              className="flex items-center gap-2 text-xs text-ink-2 dark:text-ink-dark-2"
              data-testid="kanban-workflow-owner"
            >
              <Avatar
                name={workflowOwner.nome}
                src={workflowOwner.fotoUrl ?? undefined}
                size="xs"
              />
              <span>
                Gerenciado por{' '}
                <span className="font-medium text-ink-1 dark:text-ink-dark-1">
                  {workflowOwner.nome}
                </span>
              </span>
            </div>
          ) : (
            <div />
          )}
          {(isAdmin || headerActions) && <div className="flex gap-2">{headerActions}</div>}
        </div>
      )}

      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
      >
        {/* FASE-B mobile (<640px): colunas deslizam na horizontal com scroll-snap
            — a página nunca estoura; cada coluna ocupa ~82% da largura. */}
        <div
          ref={quadroRef}
          data-testid="kanban-quadro"
          /* 🔴 A1 — O QUADRO DEVOLVE A ALTURA QUE A BARRA COMEU.
             ⚠️ Elemento fixo não empurra nada: ele DEITA por cima. Sem este
             respiro, o fim da última coluna fica sob a barra — que é o defeito
             que a régua da rolagem proíbe nominalmente ("ação nunca escondida
             atrás de scroll"). ✅ O valor é o MESMO da barra, por constante
             compartilhada: dois números iguais escritos em dois lugares
             divergem na primeira vez que alguém muda um. */
          style={{ paddingBottom: alturaBarra }}
          className={cn(
            'flex gap-4 overflow-x-auto -mx-4 px-4 snap-x snap-mandatory sm:snap-none',
            fillHeight && 'flex-1 min-h-0',
          )}
        >
          {columnsVisiveis.map((c) => (
            <KanbanColumnView
              key={c.slug}
              column={c}
              canEditLabel={isAdmin}
              canDelete={isAdmin}
              onChangeLabel={(novo) => {
                void setLabel(c.slug, novo);
              }}
              onRequestDelete={() => setColumnToDelete(c)}
              items={itemsPorColuna.get(c.slug) ?? []}
              cardComponent={CardComponent}
              showTotal={showColumnTotals}
              getItemValue={getItemValue}
              getItemValueRecorrente={getItemValueRecorrente}
              getItemHealth={getItemHealth}
              healthFilter={healthFilter}
              onToggleHealthFilter={toggleHealthFilter}
              emptyColumnText={emptyColumnText}
              emptyHint={emptyColumnHints?.[c.slug]}
              fitColumns={fitColumns}
              fillHeight={fillHeight}
            />
          ))}

          {isAdmin && (
            <AddColumnButton
              disabled={atSoftLimit}
              softLimit={SOFT_LIMIT}
              onClick={() => setCreatingColumn(true)}
            />
          )}
        </div>

        {/* 🔴 A1 (25/09/2026, CORRIGIDO na mesma noite) — A BARRA NO RODAPE.
            Ver `BarraDeRolagemDoQuadro` para o porque, os numeros e a regua
            que esta decisao RISCOU. Ela e `fixed`, entao a posicao no JSX nao
            muda onde ela aparece — fica aqui para quem ler saber de quem e. */}
        <BarraDeRolagemDoQuadro alvo={quadroRef} altura={alturaBarra} />
      </DndContext>

      {columnsVisiveis.length > 1 && (
        <p className="sm:hidden pt-1.5 text-center text-xs font-medium text-ink-3 dark:text-ink-dark-3">
          Deslize pro lado pra ver as outras fases →
        </p>
      )}

      {creatingColumn && (
        <NewColumnDialog
          onCancel={() => setCreatingColumn(false)}
          onCreate={async (label) => {
            await addColumn({ label });
            setCreatingColumn(false);
          }}
        />
      )}

      {columnToDelete && (
        <DeleteColumnDialog
          column={columnToDelete}
          cardsInColumn={items.filter((i) => getItemStage(i) === columnToDelete.slug).length}
          otherColumns={columnsVisiveis.filter((c) => c.slug !== columnToDelete.slug)}
          onCancel={() => setColumnToDelete(null)}
          onConfirm={handleConfirmDeleteColumn}
        />
      )}

      {afterContent}
    </>
  );
}

// ─── Coluna ─────────────────────────────────────────────────────────────

interface KanbanColumnViewProps<T extends { id: string }> {
  column: KanbanColumnInfo;
  canEditLabel: boolean;
  canDelete: boolean;
  onChangeLabel: (novo: string) => void;
  onRequestDelete: () => void;
  items: T[];
  cardComponent: ComponentType<KanbanCardProps<T>>;
  showTotal: boolean;
  getItemValue: (item: T) => number;
  getItemValueRecorrente?: (item: T) => number;
  getItemHealth?: (item: T) => CardHealth | null;
  healthFilter: CardHealth | null;
  onToggleHealthFilter: (target: CardHealth) => void;
  emptyColumnText: string;
  emptyHint?: ReactNode;
  fitColumns?: boolean;
  fillHeight?: boolean;
}

function KanbanColumnView<T extends { id: string }>({
  column,
  canEditLabel,
  canDelete,
  onChangeLabel,
  onRequestDelete,
  items,
  cardComponent: CardComponent,
  showTotal,
  getItemValue,
  getItemValueRecorrente,
  getItemHealth,
  healthFilter,
  onToggleHealthFilter,
  emptyColumnText,
  emptyHint,
  fitColumns = false,
  fillHeight = false,
}: KanbanColumnViewProps<T>) {
  const { setNodeRef, isOver } = useDroppable({ id: column.slug });
  const total = items.reduce((acc, i) => acc + getItemValue(i), 0);
  // PF12: soma recorrente (R$/mês) — linha própria, nunca somada no total pontual.
  const totalRecorrente = getItemValueRecorrente
    ? items.reduce((acc, i) => acc + getItemValueRecorrente(i), 0)
    : 0;

  const healthCounts = useMemo(
    () => (getItemHealth ? countItemsByHealth(items, getItemHealth) : null),
    [items, getItemHealth]
  );
  const overdueCount = healthCounts?.overdue ?? 0;
  const idleCount = healthCounts?.idle ?? 0;

  return (
    <div
      data-testid={`kanban-col-${column.slug}`}
      className={cn(
        'flex flex-col snap-start',
        // FX-19: fit → divide a largura (flex-1, sem rolagem horizontal no desktop);
        // clássico → largura fixa w-72 + rolagem horizontal (Comercial).
        // FASE-B mobile: no clássico, cada coluna ocupa ~82% da largura (<640px).
        fitColumns ? 'flex-1 min-w-[11rem] max-w-[22rem]' : 'w-[82vw] sm:w-72 flex-shrink-0',
        // FX-32: altura cheia → a coluna cabe no pai e o corpo rola verticalmente.
        fillHeight && 'min-h-0 max-h-full',
      )}
    >
      {/* C6-A #6: o cabeçalho da fase gruda no topo da coluna. Com a página
          rolando (o "escape"), sem isto o nome e a contagem da fase sumiriam da
          vista justamente quando há mais card para percorrer. */}
      {/* FASE-B (maquete scr-funil): header de coluna = card branco, borda line,
          radius 10, ALTURA MÍNIMA IGUAL pra todas (74px quando há linha de soma).
          Linha 1: dot + nome + quantidade à direita (mono 12). Linha 2: soma em
          mono 12 — ou "—" como espaço reservado, pra anatomia nunca variar. */}
      <div
        className={cn(
          'px-3.5 py-3 mb-3 rounded-[10px] bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark',
          showTotal && 'min-h-[74px]',
          // B-02 (HOTFIX-ROLAGEM): nome da fase, contagem e soma ficam visíveis
          // enquanto a coluna rola — sem isso o vendedor perde a referência de
          // onde está ao descer a coluna. `shrink-0` impede que o cabeçalho seja
          // espremido quando a coluna tem muitos cards.
          fillHeight && 'shrink-0',
        )}
      >
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <EditableColumnHeader
              slug={column.slug}
              label={column.label}
              variant={column.variant}
              canEdit={canEditLabel}
              onChange={onChangeLabel}
            />
            {!column.isDefault && (
              <Badge variant="neutral" size="sm" title="Coluna custom criada por admin">
                Custom
              </Badge>
            )}
          </div>
          <span className="ml-auto font-mono text-xs tabular-nums text-ink-3 dark:text-ink-dark-3">
            {items.length}
          </span>
          {canDelete && (
            <button
              type="button"
              data-testid={`kanban-col-${column.slug}-delete`}
              onClick={onRequestDelete}
              aria-label={`Excluir coluna ${column.label}`}
              title="Excluir coluna"
              className="text-ink-3 hover:text-red transition-colors p-1 leading-none"
            >
              ✕
            </button>
          )}
        </div>
        {showTotal && (
          <p
            data-testid={`kanban-col-total-${column.slug}`}
            className="mt-1.5 font-mono text-xs text-ink-2 dark:text-ink-dark-2 tabular-nums"
          >
            {total > 0 ? formatCurrencyCompact(total) : '—'}
          </p>
        )}
        {showTotal && totalRecorrente > 0 && (
          <p
            data-testid={`kanban-col-total-mensal-${column.slug}`}
            className="font-mono text-xs text-red dark:text-red-glow tabular-nums"
          >
            {formatCurrencyCompact(totalRecorrente)}/mês
          </p>
        )}

        {(overdueCount > 0 || idleCount > 0) && (
          <div className="flex flex-wrap gap-1 mt-2">
            {overdueCount > 0 && (
              <button
                type="button"
                data-testid={`kanban-col-${column.slug}-overdue-badge`}
                aria-pressed={healthFilter === 'overdue'}
                onClick={() => onToggleHealthFilter('overdue')}
                className="cursor-pointer hover:opacity-80 transition-opacity"
                title="Filtrar visualmente cards vencidos"
              >
                <Badge variant="danger" size="sm">
                  🔴 {overdueCount} {overdueCount === 1 ? 'vencido' : 'vencidos'}
                </Badge>
              </button>
            )}
            {idleCount > 0 && (
              <button
                type="button"
                data-testid={`kanban-col-${column.slug}-idle-badge`}
                aria-pressed={healthFilter === 'idle'}
                onClick={() => onToggleHealthFilter('idle')}
                className="cursor-pointer hover:opacity-80 transition-opacity"
                title="Filtrar visualmente cards parados"
              >
                <Badge variant="warning" size="sm">
                  🟠 {idleCount} {idleCount === 1 ? 'parado' : 'parados'}
                </Badge>
              </button>
            )}
          </div>
        )}
      </div>

      <div
        ref={setNodeRef}
        className={cn(
          // FASE-B: os cards assentam direto no papel (sem "painel" com wash),
          // gap 12px entre cards — como na maquete. O highlight de drop fica.
          'flex-1 min-h-[120px] rounded-[10px] p-1 -m-1 space-y-3 transition-colors',
          // FX-19: cada coluna rola verticalmente dentro de si (barra no rodapé do quadro).
          // B-02: com altura cheia o `max-h` mágico sai de cena — ele era
          // calculado no chute (100vh-14rem) e cortava o último card quando o
          // cabeçalho real tinha outra altura.
          fitColumns && !fillHeight && 'overflow-y-auto max-h-[calc(100vh-14rem)]',
          // FX-32: altura cheia → o corpo rola dentro da coluna (barra horizontal fixa no rodapé).
          // B-02: `min-h-0` é o que deixa o flex-1 realmente encolher (sem ele o
          // `min-h-[120px]` vence e o corpo empurra a coluna para fora da tela);
          // `pb-2` dá o respiro que faltava para o último card não colar no fim.
          // C6-A #6 (08/08/2026) — o PISO da coluna.
          //
          // Era só `min-h-0`, e ele sobrescrevia o `min-h-[120px]` acima (mesma
          // propriedade; o tailwind-merge deixa o último vencer). Resultado
          // medido: `min-height: 0px` em TODAS as viewports, e a coluna
          // encolhendo com a janela — 655px em 1080p, 343px em 768, 249px em
          // 720, 168px em 375. Com card de ~216px, isso é 1 card por coluna no
          // notebook e NENHUM card inteiro no celular.
          //
          // `--igm-kanban-piso` vale ~3 cards (o "~3" do pedido). Quando a
          // janela não comporta esse piso, a coluna para de encolher e o
          // container — que agora é `min-h` — deixa a PÁGINA rolar.
          // O piso NÃO mora aqui: mora no container (`--igm-kanban-quadro-min`).
          // Tentei pôr nesta linha primeiro e o resultado foi pior que o
          // problema — sem altura definida no pai, o `flex-1` crescia com o
          // conteúdo, a coluna parava de rolar e a página rolava 1742px de uma
          // vez. `min-h-0` é o que deixa o flex encolher até o limite que o
          // container permite.
          fillHeight && 'overflow-y-auto min-h-0 pb-2',
          // EXPERIMENTO NAO COMMITADO (08/08/2026) — scroll-snap por card,
          // para o Matheus avaliar usando. A rolagem gruda no comeco de um
          // card, entao nenhum card fica cortado no topo.
          // Se aprovado, entra em commit proprio; se nao, sai sem rastro.
          // Testar ARRASTANDO com a coluna rolada — snap e drag brigam.
          fillHeight && 'snap-y snap-mandatory [&>*]:snap-start',
          isOver && 'bg-red-soft/40 dark:bg-red-soft-dark/40 ring-2 ring-red',
        )}
      >
        {items.length === 0 ? (
          emptyHint ? (
            <div className="py-4 px-2 text-center" data-testid={`kanban-empty-hint-${column.slug}`}>
              {emptyHint}
            </div>
          ) : (
            <p className="text-xs text-ink-3 dark:text-ink-dark-3 italic text-center py-6">
              {emptyColumnText}
            </p>
          )
        ) : (
          items.map((item) => (
            <CardComponent
              key={item.id}
              item={item}
              healthFilter={healthFilter}
              column={column}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Botão "+ Nova coluna" ──────────────────────────────────────────────

function AddColumnButton({
  disabled,
  softLimit,
  onClick,
}: {
  disabled: boolean;
  softLimit: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-testid="kanban-add-column"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabled ? `Limite de ${softLimit} colunas atingido` : 'Criar nova coluna'}
      className={cn(
        'w-64 flex-shrink-0 mt-[3.5rem] rounded-xl border-2 border-dashed text-sm transition-colors',
        'flex items-center justify-center gap-2 py-4',
        disabled
          ? 'border-line dark:border-line-dark text-ink-3 dark:text-ink-dark-3 opacity-60 cursor-not-allowed'
          : 'border-line dark:border-line-dark text-ink-2 dark:text-ink-dark-2 hover:border-red hover:text-red dark:hover:text-red-glow'
      )}
    >
      <span className="text-base leading-none">+</span>
      <span>Nova coluna</span>
    </button>
  );
}

// ─── Modal: Nova coluna ────────────────────────────────────────────────

const newColumnSchema = z.object({
  label: z.string().trim().min(2, 'Mínimo 2 caracteres').max(30, 'Máximo 30 caracteres'),
});

type NewColumnValues = z.infer<typeof newColumnSchema>;

function NewColumnDialog({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (label: string) => void | Promise<void>;
}) {
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<NewColumnValues>({
    resolver: zodResolver(newColumnSchema),
    defaultValues: { label: '' },
  });

  async function onSubmit(values: NewColumnValues) {
    setSubmitError(null);
    try {
      await onCreate(values.label);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Erro ao criar coluna');
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()} size="sm">
      <DialogHeader>
        <DialogTitle>Nova coluna</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <div className="space-y-3">
            {submitError && (
              <div className="p-3 rounded-xl bg-red-soft border-l-2 border-red text-red-deep text-sm">
                {submitError}
              </div>
            )}
            <div>
              <Label htmlFor="new-column-label">Nome da coluna</Label>
              <Input
                id="new-column-label"
                autoFocus
                placeholder="Ex: Em revisão"
                error={errors.label?.message}
                hint="Slug é gerado automaticamente do nome (snake_case). Você pode renomear depois sem perder os cards."
                {...register('label')}
              />
            </div>
          </div>
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" loading={isSubmitting}>
            Criar coluna
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

// ─── Modal: Excluir coluna ─────────────────────────────────────────────

function DeleteColumnDialog({
  column,
  cardsInColumn,
  otherColumns,
  onCancel,
  onConfirm,
}: {
  column: KanbanColumnInfo;
  cardsInColumn: number;
  otherColumns: KanbanColumnInfo[];
  onCancel: () => void;
  onConfirm: (targetSlug: string | null) => void | Promise<void>;
}) {
  const hasCards = cardsInColumn > 0;
  const [moveTo, setMoveTo] = useState<string>(otherColumns[0]?.slug ?? '');
  const [submitting, setSubmitting] = useState(false);

  const canConfirm = hasCards ? !!moveTo : true;

  async function handleConfirm() {
    setSubmitting(true);
    try {
      await onConfirm(hasCards ? moveTo : null);
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    if (hasCards && !moveTo && otherColumns[0]) {
      setMoveTo(otherColumns[0].slug);
    }
  }, [hasCards, moveTo, otherColumns]);

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()} size="sm">
      <DialogHeader>
        <DialogTitle>Excluir coluna &ldquo;{column.label}&rdquo;?</DialogTitle>
      </DialogHeader>
      <DialogContent>
        {hasCards ? (
          <>
            <p className="text-sm text-ink-2 dark:text-ink-dark-2 mb-3">
              Esta coluna tem <strong>{cardsInColumn}</strong>{' '}
              {cardsInColumn === 1 ? 'card' : 'cards'}. Pra preservar os dados,
              vamos mover {cardsInColumn === 1 ? 'ele' : 'eles'} pra outra coluna
              antes de excluir.
            </p>
            <div>
              <Label htmlFor="delete-column-move-to">Mover cards para</Label>
              <Select
                id="delete-column-move-to"
                value={moveTo}
                onChange={(e) => setMoveTo(e.target.value)}
              >
                {otherColumns.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.label}
                  </option>
                ))}
              </Select>
            </div>
          </>
        ) : (
          <p className="text-sm text-ink-2 dark:text-ink-dark-2">
            Esta coluna está vazia. A exclusão é segura — não há cards pra mover.
          </p>
        )}
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="destructive"
          loading={submitting}
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          {hasCards ? `Mover ${cardsInColumn} e excluir` : 'Excluir coluna'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
